/**
 * Data Binding MetaModel — v2 schema layer.
 *
 * Describes which Home-Assistant-style binding *strategies* exist and
 * which of them a widget property supports. This is capability
 * metadata, the same kind `shared.js`/`stateFormat.js`/`wireFormat.js`
 * already hold at this location — provider-independent, and owned by
 * the V2 schema layer rather than the automation/Home-Assistant layer.
 * The automation layer (editors, generators) imports this file; it
 * does not define anything here.
 *
 * See docs/Binding_Architecture_FINAL.md for the full architecture.
 *
 * A binding strategy describes BEHAVIOR ONLY:
 *  - label     — display name for the strategy picker
 *  - editor    — which Vue component edits a binding using this strategy
 *  - generator — which generator function serializes it
 *
 * It deliberately does NOT describe payload shape (no `data` field).
 * Each strategy's concrete fields live on AutomationBinding itself
 * (flat, see AutomationBinding.js) and are read directly by that
 * strategy's editor/generator — a strategy names components, it does
 * not describe their internals. (An earlier draft of this file
 * declared a per-strategy `data` schema; that approach was reviewed
 * and rejected in favor of this simpler one — see
 * Binding_Architecture_FINAL.md §1.)
 */
export const BINDING_STRATEGIES = {
    constant: {
        label:     'Constant',
        editor:    'ConstantBindingEditor',
        generator: 'constant',
    },

    state: {
        label:     'Entity State',
        editor:    'StateBindingEditor',
        generator: 'state',
    },

    mapping: {
        label:     'Mapping',
        editor:    'MappingBindingEditor',
        generator: 'mapping',
    },
};

/**
 * The 'template' strategy — a raw Jinja2 expression. Deliberately kept out
 * of BINDING_STRATEGIES (and therefore out of availableStrategies()/the
 * Strategy dropdown): it's an advanced escape hatch, not a peer of
 * Constant/State/Mapping. Editors reach it through its own dedicated UI
 * entry point (see StateBindingsTable.vue), never through the same
 * strategy picker used for the structured strategies above.
 */
export const ADVANCED_STRATEGY = {
    key:       'template',
    label:     'Advanced Template',
    editor:    'TemplateBindingEditor',
    generator: 'template',
};

/**
 * Every strategy's metadata, structured + advanced, keyed by strategy name.
 * For consumers that must resolve *any* binding regardless of which kind
 * of strategy it holds (the editor router, the collapsed-row presentation
 * badge) — never used to populate the Strategy dropdown, which reads
 * BINDING_STRATEGIES directly so 'template' can never appear there.
 */
export const ALL_STRATEGIES = {
    ...BINDING_STRATEGIES,
    [ADVANCED_STRATEGY.key]: ADVANCED_STRATEGY,
};

/**
 * Default binding strategies available to a property, keyed by the
 * property's own `type` (schema.properties[name].type /
 * schema.parts.*.properties[name].type — the same `type` values
 * PropertyEditorMeta.js's leaf-editor lookup (`_editorFor`) already
 * switches on).
 *
 * NOTE — this table and PropertyEditorMeta.js's `_editorFor` switch both
 * branch on that same property-type vocabulary independently (one picks
 * a leaf editor, this one picks available strategies); neither is
 * derived from the other. Adding a new property `type` should update both.
 *
 * A widget schema only needs an explicit `bindings.properties.<name>.strategies`
 * override when this default list is wrong for that specific property —
 * see Binding_Architecture_FINAL.md §2.
 */
export const DEFAULT_STRATEGIES_BY_TYPE = {
    bool:     ['constant', 'mapping', 'state'],
    enum:     ['constant', 'mapping', 'state'],
    color:    ['constant', 'mapping', 'state'],
    int:      ['constant', 'state', 'mapping'],
    float:    ['constant', 'state', 'mapping'],
    // 'mapping' is available here too: a mapping's *condition* side reads the
    // entity state, its *result* side is typed by the target property (see
    // MappingRuleRow.vue → PropertyEditorFactory, driven by
    // PropertyEditorMeta.js). Source and result types are independent, so a
    // numeric entity can drive numeric comparison rules that emit string
    // results into a string property (e.g. label.text: < -5 → "Laden").
    string:   ['constant', 'state', 'mapping'],
    resource: ['constant'],
};

/** Used when a property's type has no entry in DEFAULT_STRATEGIES_BY_TYPE. */
const FALLBACK_STRATEGIES = ['constant', 'state'];

/**
 * Resolve the binding strategies available to one property.
 *
 * Pure lookup — takes the already-resolved property type and the
 * property's own `bindings.properties.<name>` entry (if any); it does
 * not walk a widget schema itself. Resolving `propertyType` from a
 * schema (`schema.properties[name]` then `schema.parts.*.properties[name]`)
 * is the caller's job — see PropertyEditorMeta.js's `_findPropertyDef`,
 * which already performs that exact traversal.
 *
 * @param {string} propertyType — the property's schema `type` (e.g. 'color', 'int')
 * @param {{ strategies?: string[] }} [bindingDef] — the property's
 *        `bindings.properties.<name>` entry; `strategies` overrides the default
 * @returns {string[]} strategy keys, e.g. ['constant', 'mapping', 'state']
 */
export function availableStrategies(propertyType, bindingDef) {
    return bindingDef?.strategies
        ?? DEFAULT_STRATEGIES_BY_TYPE[propertyType]
        ?? FALLBACK_STRATEGIES;
}
